<template>
    <span class="RevertActionButton">
        <RbacDisableDefaultGuard :features="permissionsToRevert">
            <NewButton
                class="RevertActionButton__button"
                variant="outline"
                color="danger"
                :disabled="!revertable"
                :aria-describedby="tooltip ? reasonId : undefined"
                @click="$emit('revert')"
            >
                Revert
            </NewButton>
        </RbacDisableDefaultGuard>
        <!-- Visible text, not a title attribute: a disabled button takes no focus and a title on the
             wrapping span reaches neither the keyboard nor a screen reader, so the reason why Revert is
             unavailable was mouse-only - on a tab where most rows are not revertable. -->
        <span v-if="tooltip" :id="reasonId" class="RevertActionButton__reason">
            {{ tooltip }}
        </span>
    </span>
</template>

<script lang="ts">
    import { defineComponent, PropType } from 'vue';

    import RbacDisableDefaultGuard
        from '@evidentid/dashboard-commons/modules/role-base-access-control/components/rbac-guard/RbacDisableDefaultGuard.vue';
    import { RbacFeature } from '@evidentid/tprm-portal-lib/models/rbac';

    import NewButton from '@/components/new-button/NewButton.vue';
    import { REVERT_NOT_REVERTABLE_MESSAGE } from '@/modules/entity-actions-review/constants';

    let nextReasonId = 0;

    export default defineComponent({
        name: 'RevertActionButton',
        components: { NewButton, RbacDisableDefaultGuard },
        props: {
            /* Comes straight from the API rather than being derived from status or resolution
               data here, because the backend is the only source that agrees with what the
               revert endpoint will actually accept. An action completed before the audit table
               existed is revertable like any other -- the revert writes the resolution row it
               never had -- so do not gate this on a resolution being present. */
            revertable: {
                type: Boolean as PropType<boolean>,
                default: false,
            },
            notRevertableReason: {
                type: String as PropType<string | null>,
                default: null,
            },
            globalScope: {
                type: Boolean as PropType<boolean>,
                default: false,
            },
        },
        emits: [ 'revert' ],
        data() {
            // Unique per instance so a list of rows does not point every button at the same element.
            nextReasonId += 1;
            return { reasonId: `RevertActionButton__reason-${nextReasonId}` };
        },
        computed: {
            /* Undefined rather than an empty string so a revertable control carries no reason text
               at all. */
            tooltip(): string | undefined {
                if (this.revertable) {
                    return undefined;
                }
                return this.notRevertableReason || REVERT_NOT_REVERTABLE_MESSAGE;
            },
            permissionsToRevert(): RbacFeature[] {
                return this.globalScope
                    ? [ RbacFeature.actionsResolveGlobalAction ]
                    : [ RbacFeature.actionsResolveEntityAction ];
            },
        },
    });
</script>
